import {
  Avatar,
  makeStyles,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  Building20Regular,
  LocalLanguage20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
} from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';

import { useAuth } from '../lib/auth/hooks';
import { useI18n } from '../lib/i18n/hooks';
import { useActiveOrganization } from '../lib/queries/useActiveOrganization';
import { useSetActiveOrganization } from '../lib/queries/useSetActiveOrganization';
import { useTheme } from '../lib/theme/ThemeContext';
import { LanguageSwitcher } from './LanguageSwitcher';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    gap: tokens.spacingHorizontalS,
    border: 'none',
    background: 'transparent',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXL}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: '#ffffff',
      outlineStyle: 'solid',
      outlineOffset: '2px',
    },
  },
  triggerContent: {
    display: 'none',
    '@media (min-width: 640px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '2px',
      paddingRight: tokens.spacingHorizontalS,
    },
  },
  userEmail: {
    fontSize: tokens.fontSizeBase200,
    color: '#ffffff',
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap',
  },
  organizationName: {
    fontSize: tokens.fontSizeBase100,
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: tokens.lineHeightBase100,
    whiteSpace: 'nowrap',
  },
  popover: {
    maxWidth: 'calc(100vw - 16px)',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  userName: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userEmailInPopover: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  organizationNameInPopover: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  languageMenuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalSNudge}`,
  },
  languageLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    color: tokens.colorNeutralForeground1,
    flexShrink: 0,
  },
  dropdownWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: 1,
  },
  dropdownButton: {
    justifyContent: 'flex-end',
  },
  themeMenuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalSNudge}`,
  },
  themeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    color: tokens.colorNeutralForeground1,
    flexShrink: 0,
  },
});

export function UserMenu() {
  const styles = useStyles();
  const { data: session } = useAuth();
  const { data: organization } = useActiveOrganization();
  const { mutate: setActiveOrganization } = useSetActiveOrganization();
  const { t } = useI18n();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();

  if (!session?.user) {
    return null;
  }

  // org-admin never clears its organization
  const isAdmin = session.user.role === 'admin';
  const hasActiveOrg = !!session.session?.activeOrganizationId;
  const defaultOrganizationId =
    (session.session as { defaultOrganizationId?: string | null } | undefined)?.defaultOrganizationId ?? null;
  const isOnNonDefaultOrg = hasActiveOrg && session.session?.activeOrganizationId !== defaultOrganizationId;

  const initials =
    session.user.name
      ?.split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase() || session.user.email[0].toUpperCase();

  const handleSignOut = () => {
    void navigate({ to: '/logout' });
  };

  const handleSwitchOrganization = () => {
    void navigate({ to: '/settings/switch-organization' });
  };

  const handleClearActiveOrganization = () => {
    setActiveOrganization(defaultOrganizationId);
  };

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button className={styles.trigger} aria-label={t('user-menu.aria-label')} title={session.user.email}>
          <div className={styles.triggerContent}>
            <span className={styles.userEmail}>{session.user.email}</span>
            {organization && <span className={styles.organizationName}>{organization.name}</span>}
          </div>
          <Avatar name={session.user.name || session.user.email} initials={initials} color="colorful" size={32} />
        </button>
      </MenuTrigger>
      <MenuPopover className={styles.popover}>
        <div className={styles.userInfo}>
          <Avatar name={session.user.name || session.user.email} initials={initials} color="colorful" size={56} />
          <div className={styles.userDetails}>
            {session.user.name && <Text className={styles.userName}>{session.user.name}</Text>}
            <Text className={styles.userEmailInPopover}>{session.user.email}</Text>
            {organization && (
              <Text className={styles.organizationNameInPopover}>
                {organization.name} ({organization.slug})
              </Text>
            )}
          </div>
        </div>
        <MenuDivider />
        <MenuItem icon={<Building20Regular />} onClick={handleSwitchOrganization}>
          {t('user-menu.switch-organization')}
        </MenuItem>
        {isAdmin && isOnNonDefaultOrg && (
          <MenuItem icon={<Building20Regular />} onClick={handleClearActiveOrganization}>
            {t('user-menu.clear-organization')}
          </MenuItem>
        )}
        <MenuDivider />
        <div className={styles.languageMenuItem}>
          <div className={styles.languageLabel}>
            <LocalLanguage20Regular />
            <span>{t('user-menu.language')}</span>
          </div>
          <div className={styles.dropdownWrapper}>
            <LanguageSwitcher className={styles.dropdownButton} />
          </div>
        </div>
        <div className={styles.themeMenuItem}>
          <div className={styles.themeLabel}>
            <WeatherMoon20Regular />
            <span>{t('user-menu.appearance')}</span>
          </div>
          <div className={styles.dropdownWrapper}>
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <MenuButton appearance="subtle" className={styles.dropdownButton}>
                  {t(`user-menu.appearance-${mode}`)}
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => setMode('system')}>{t('user-menu.appearance-system')}</MenuItem>
                  <MenuItem onClick={() => setMode('light')}>{t('user-menu.appearance-light')}</MenuItem>
                  <MenuItem onClick={() => setMode('dark')}>{t('user-menu.appearance-dark')}</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        </div>
        <MenuDivider />
        <MenuItem icon={<SignOut20Regular />} onClick={handleSignOut}>
          {t('user-menu.sign-out')}
        </MenuItem>
      </MenuPopover>
    </Menu>
  );
}
